// src/components/Upload/MultipleFileUploadField.js

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';

import { Button, Grid, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import SingleFileUploadWithProgress from './SingleFileUploadWithProgress';
import UploadError from './UploadError';

//----------------------------------- CONSTANTS -----------------------------------//

export const DEFAULT_ACCEPTED_FILE_TYPES = ['.pdf', '.jpg', '.jpeg', '.png', '.svg', '.docx'];

export const IMAGE_ACCEPTED_FILE_TYPES = ['.jpg', '.jpeg', '.png', '.svg'];

export const COMPANY_IMAGE_MAX_FILE_SIZE = 10 * 1024 * 1024;

const DEFAULT_MAX_FILE_SIZE = 1024 * 1024;

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',

  minHeight: 52,
  padding: 10,

  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',

  backgroundColor: '#fafafa',
  color: '#bdbdbd',

  outline: 'none',
  cursor: 'pointer',

  transition: 'border-color 0.24s ease-in-out',
};

const activeStyle = {
  borderColor: '#2196f3',
};

const acceptStyle = {
  borderColor: '#00e676',
};

const rejectStyle = {
  borderColor: '#ff1744',
};

const disabledStyle = {
  cursor: 'not-allowed',
  opacity: 0.6,
};

//----------------------------------- UTILS -----------------------------------//

const getFileKey = (file) => {
  return [file?.name, file?.size, file?.lastModified].join('-');
};

const getUploadedFileId = (response) => {
  return response?.unique_field ?? response?.imageid ?? null;
};

//----------------------------------- COMPONENT -----------------------------------//

const MultipleFileUploadField = ({
  variant = 'generic',

  currentFileId = null,

  disabled = false,

  multiple = false,

  clearAfterUpload = true,

  acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,

  maxFileSize = DEFAULT_MAX_FILE_SIZE,

  onUploaded,
}) => {
  const { t } = useTranslation();

  const [files, setFiles] = useState([]);

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      const accepted = acceptedFiles.map((file) => ({
        key: getFileKey(file),
        file,
        errors: [],
        uploaded: false,
      }));

      const rejected = rejectedFiles.map(({ file, errors }) => ({
        key: getFileKey(file),
        file,
        errors,
        uploaded: false,
      }));

      const newFiles = [...accepted, ...rejected];

      setFiles((current) => {
        if (!multiple) {
          return newFiles.slice(0, 1);
        }

        return [...current, ...newFiles];
      });
    },
    [multiple]
  );

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    maxSize: maxFileSize,
    multiple,
    disabled,
  });

  const dropzoneStyle = useMemo(
    () => ({
      ...baseStyle,

      ...(isDragActive ? activeStyle : {}),

      ...(isDragAccept ? acceptStyle : {}),

      ...(isDragReject ? rejectStyle : {}),

      ...(disabled ? disabledStyle : {}),
    }),
    [disabled, isDragAccept, isDragActive, isDragReject]
  );

  const handleUpload = useCallback(
    async (file, response) => {
      const uploadedFileId = getUploadedFileId(response);

      if (!uploadedFileId) {
        throw new Error('Upload response does not contain a file identifier.');
      }

      if (typeof onUploaded === 'function') {
        const result = await onUploaded(uploadedFileId, response);

        /*
         * Parent callbacks return null/false
         * when assignment failed.
         */
        if (result === null || result === false) {
          return false;
        }
      }

      setFiles((current) =>
        current.map((fileWrapper) =>
          fileWrapper.file === file
            ? {
                ...fileWrapper,
                id: response?.id,
                imageid: uploadedFileId,
                uploaded: true,
              }
            : fileWrapper
        )
      );

      if (clearAfterUpload) {
        setFiles((current) => current.filter((fileWrapper) => fileWrapper.file !== file));
      }

      return true;
    },
    [clearAfterUpload, onUploaded]
  );

  const handleDelete = useCallback((file) => {
    setFiles((current) => current.filter((fileWrapper) => fileWrapper.file !== file));
  }, []);

  const buttonText = useMemo(() => {
    switch (variant) {
      case 'avatar':
        return currentFileId ? t('button.change_avatar') : t('button.upload_avatar');

      case 'company-logo':
      case 'company-stamp':
        if (disabled) {
          return t('button.create_profile_before_upload_image');
        }

        return currentFileId ? t('button.change') : t('button.upload');

      default:
        return t('button.drag_and_drop');
    }
  }, [currentFileId, disabled, t, variant]);

  const renderDropzoneContent = () => {
    if (variant === 'attachment') {
      return (
        <>
          <AttachFileIcon />

          <Typography variant="caption">{t('button.drag_and_drop')}</Typography>
        </>
      );
    }

    if (variant === 'avatar' || variant === 'company-logo' || variant === 'company-stamp') {
      return (
        <Button type="button" color="primary" variant="text" fullWidth disabled={disabled} tabIndex={-1}>
          <Typography variant="button">{buttonText}</Typography>
        </Button>
      );
    }

    return <Typography variant="body2">{buttonText}</Typography>;
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <div
          {...getRootProps({
            style: dropzoneStyle,
          })}
        >
          <input {...getInputProps()} />

          {renderDropzoneContent()}
        </div>
      </Grid>

      {files.map((fileWrapper) => (
        <Grid item xs={12} key={fileWrapper.key}>
          {fileWrapper.errors.length > 0 ? (
            <UploadError
              file={fileWrapper.file}
              errors={fileWrapper.errors}
              onDelete={handleDelete}
              maxFileSize={maxFileSize}
              acceptedFileTypes={acceptedFileTypes}
            />
          ) : (
            <SingleFileUploadWithProgress file={fileWrapper.file} onUpload={handleUpload} onDelete={handleDelete} />
          )}
        </Grid>
      ))}
    </Grid>
  );
};

export default MultipleFileUploadField;
